import type { ReactNode } from 'react';

import { Tooltip } from '@components';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { PaymentOrderTermType } from '@types';

const gstPctLabel = (name: string, pct: string | null | undefined) => {
  const p = pct?.trim();
  return p ? `${name} (${p}%)` : name;
};

const SectionDivider = ({ dashed }: { dashed?: boolean }) => (
  <div
    className={`my-2 ${
      dashed
        ? 'border-t border-dashed border-cofeeNeutralInactive'
        : 'border-t border-cofeeNeutralInactive'
    }`}
  />
);

type RowProps = {
  label: string;
  labelClassName?: string;
  amount: number;
  isDefaultCurrency: boolean;
  negate?: boolean;
  valueClassName?: string;
  symbolClassName?: string;
};

const BreakdownMoneyRow = ({
  label,
  labelClassName = 'text-cofeeNeutral10',
  amount,
  isDefaultCurrency,
  negate,
  valueClassName = 'font-medium text-content',
  symbolClassName
}: RowProps) => {
  const defaultSymbolClass = isDefaultCurrency
    ? 'text-[0.95em] !font-normal !text-content'
    : '!text-xs !font-medium !text-content';

  return (
    <div className="flex justify-between gap-4 text-xs leading-relaxed">
      <span className={labelClassName}>{label}</span>
      <span
        className={`inline-flex shrink-0 items-center justify-end gap-0.5 tabular-nums ${valueClassName}`}
      >
        {negate ? '-' : ''}
        <CurrencyAmount
          value={Math.abs(amount)}
          className={`${
            isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
          } justify-end`}
          symbolClass={symbolClassName ?? defaultSymbolClass}
          valueClass={valueClassName}
        />
      </span>
    </div>
  );
};

type FeeSplitEntry = NonNullable<PaymentOrderTermType['fee_split']>[number];
export type InstallmentBreakdownEntry = {
  label: string;
  preTaxAmount: number;
  isDisabled?: boolean;
};

const feeSplitRowKey = (item: FeeSplitEntry) =>
  `split-${item.branch_fee_category_id}-${item.account_detail_id}-${item.category}-${item.base_amount}`;

const feeSplitDisplayLabel = (item: FeeSplitEntry): string => {
  const name = item.category?.trim() ?? '';
  if (name) return name;
  return `Fee category`;
};

const installmentBreakdownRowKey = (item: InstallmentBreakdownEntry) =>
  `installment-${item.label}-${item.preTaxAmount}`;

export const TermInstallmentBreakdownPanel = ({
  row,
  isDefaultCurrency,
  installmentBreakdown
}: {
  row: PaymentOrderTermType;
  isDefaultCurrency: boolean;
  installmentBreakdown?: InstallmentBreakdownEntry[];
}) => {
  const termAmountDetails = row.original_amount_requested_details;
  const feeSplits =
    row.fee_split?.filter(
      (fee) => Number(fee.base_amount ?? fee.amount) !== 0
    ) ?? [];
  const hasInstallmentBreakdown = (installmentBreakdown?.length ?? 0) > 0;

  const subtotalCategories = Number(
    termAmountDetails?.base_amount ?? termAmountDetails?.total_amount ?? 0
  );

  const cgst = Number(termAmountDetails?.cgst_amount ?? 0);
  const sgst = Number(termAmountDetails?.sgst_amount ?? 0);
  const igst = Number(termAmountDetails?.igst_amount ?? 0);
  const hasGstBand = cgst > 0 || sgst > 0 || igst > 0;
  const totalGst = cgst + sgst + igst;

  const discount = Number(termAmountDetails?.total_discount_amount ?? 0);
  const fine = Number(termAmountDetails?.fine_amount ?? 0);
  const closed = Number(termAmountDetails?.closed_amount ?? 0);

  const totalAmount = Number(row.amount ?? 0);
  const paidTillAmount = Number(row.paid_amount ?? 0);
  const remainingAmount = Number(
    row.pending_amount ?? row.remaining_amount ?? 0
  );

  const gstNeedsTopSpacing =
    hasInstallmentBreakdown ||
    feeSplits.length > 0 ||
    (subtotalCategories != null && subtotalCategories > 0) ||
    fine > 0;

  const hasLeadingBreakdownRows =
    hasInstallmentBreakdown ||
    feeSplits.length > 0 ||
    (subtotalCategories != null && subtotalCategories > 0) ||
    fine > 0 ||
    hasGstBand ||
    discount > 0 ||
    closed > 0;

  return (
    <div className="modal-scroll max-h-[60vh] overflow-y-auto px-4 py-3 text-left font-normal">
      {hasInstallmentBreakdown
        ? installmentBreakdown?.map((item) => {
            const disabledSymbol = isDefaultCurrency
              ? 'text-sm !font-normal !text-current'
              : '!text-xs !font-medium !text-current';
            const moneyRow = (
              <BreakdownMoneyRow
                label={item.label}
                amount={Number(item.preTaxAmount ?? 0)}
                isDefaultCurrency={isDefaultCurrency}
                labelClassName={
                  item.isDisabled ? 'text-current' : 'text-cofeeNeutral10'
                }
                valueClassName={
                  item.isDisabled
                    ? 'font-medium text-current'
                    : 'font-medium text-content'
                }
                symbolClassName={item.isDisabled ? disabledSymbol : undefined}
              />
            );
            return (
              <div key={installmentBreakdownRowKey(item)} className="mt-0.5">
                {item.isDisabled ? (
                  <div className="text-grey line-through">{moneyRow}</div>
                ) : (
                  moneyRow
                )}
                <SectionDivider dashed />
              </div>
            );
          })
        : feeSplits.map((item) => (
            <div key={feeSplitRowKey(item)} className="mt-0.5">
              <BreakdownMoneyRow
                label={feeSplitDisplayLabel(item)}
                amount={Number(item.base_amount ?? item.amount ?? 0)}
                isDefaultCurrency={isDefaultCurrency}
              />
              <SectionDivider dashed />
            </div>
          ))}

      {subtotalCategories != null ? (
        <BreakdownMoneyRow
          label="Subtotal"
          amount={subtotalCategories}
          isDefaultCurrency={isDefaultCurrency}
          labelClassName="font-medium text-content"
        />
      ) : null}

      {fine > 0 ? (
        <div className="mt-2">
          <SectionDivider dashed />
          <BreakdownMoneyRow
            label="Fine"
            amount={fine}
            isDefaultCurrency={isDefaultCurrency}
            valueClassName="font-medium text-cofeeRed4"
            symbolClassName="text-cofeeRed4 font-medium"
          />
        </div>
      ) : null}

      {hasGstBand ? (
        <div className={gstNeedsTopSpacing ? 'mt-2' : ''}>
          <SectionDivider dashed />
          <div className="space-y-2">
            {cgst > 0 ? (
              <BreakdownMoneyRow
                label={gstPctLabel('CGST', termAmountDetails?.cgst)}
                amount={cgst}
                isDefaultCurrency={isDefaultCurrency}
              />
            ) : null}
            {sgst > 0 ? (
              <BreakdownMoneyRow
                label={gstPctLabel('SGST', termAmountDetails?.sgst)}
                amount={sgst}
                isDefaultCurrency={isDefaultCurrency}
              />
            ) : null}
            {igst > 0 ? (
              <BreakdownMoneyRow
                label={gstPctLabel('IGST', termAmountDetails?.igst)}
                amount={igst}
                isDefaultCurrency={isDefaultCurrency}
              />
            ) : null}
            <SectionDivider dashed />
            <BreakdownMoneyRow
              label="Total GST"
              amount={totalGst}
              isDefaultCurrency={isDefaultCurrency}
              labelClassName="font-medium text-content"
            />
          </div>
        </div>
      ) : null}

      {discount > 0 ? (
        <div className="mt-2">
          <SectionDivider dashed={false} />
          <BreakdownMoneyRow
            label="Discount"
            amount={discount}
            isDefaultCurrency={isDefaultCurrency}
            negate
          />
        </div>
      ) : null}

      {closed > 0 ? (
        <div className="mt-2">
          <BreakdownMoneyRow
            label="Closed amount"
            amount={closed}
            isDefaultCurrency={isDefaultCurrency}
            negate
          />
        </div>
      ) : null}

      {hasLeadingBreakdownRows ? <SectionDivider dashed /> : null}
      <BreakdownMoneyRow
        label="Total amount"
        amount={totalAmount}
        isDefaultCurrency={isDefaultCurrency}
        labelClassName="text-sm font-semibold text-content"
        valueClassName="text-sm font-semibold text-content"
      />

      <SectionDivider dashed={false} />
      <BreakdownMoneyRow
        label="Total amount paid till date"
        amount={paidTillAmount}
        isDefaultCurrency={isDefaultCurrency}
      />
      <div className="mt-2">
        <SectionDivider dashed={false} />
        <BreakdownMoneyRow
          label="Remaining balance"
          amount={remainingAmount}
          isDefaultCurrency={isDefaultCurrency}
          labelClassName="text-cofeeNeutral10"
          valueClassName="font-semibold text-cofeeRed4"
          symbolClassName={
            isDefaultCurrency
              ? 'text-[0.95em] !font-normal !text-cofeeRed4'
              : '!text-xs !font-medium !text-cofeeRed4'
          }
        />
      </div>
    </div>
  );
};

export const shouldShowTermInstallmentAmountTooltip = (
  row: PaymentOrderTermType,
  currentTermAmount?: string | null
): boolean => {
  if (currentTermAmount == null) {
    return false;
  }

  const feeSplits =
    row.fee_split?.filter(
      (fee) => Number(fee.base_amount ?? fee.amount) !== 0
    ) ?? [];
  if (feeSplits.length > 0) return true;

  const originalRequestedTotal = Number(
    row.original_amount_requested_details?.base_amount ?? 0
  );
  const payableAmount = Number(row.pending_amount ?? 0);
  return originalRequestedTotal !== payableAmount;
};

type TermBreakdownTooltipProps = {
  id: string;
  row: PaymentOrderTermType;
  isDefaultCurrency: boolean;
  installmentBreakdown?: InstallmentBreakdownEntry[];
  children: ReactNode;
};

const TermBreakdownTooltip = ({
  id,
  row,
  isDefaultCurrency,
  installmentBreakdown,
  children
}: TermBreakdownTooltipProps) => (
  <>
    <span data-tooltip-id={id} className="inline-flex cursor-default">
      {children}
    </span>
    <Tooltip
      id={id}
      type="custom"
      variant="light"
      className="!z-40 !max-w-[min(92vw,26rem)] !border !border-cofeeNeutralInactive !bg-white !p-0 !text-xs !text-content !shadow-lg"
      place="top"
      clickable
    >
      <TermInstallmentBreakdownPanel
        row={row}
        isDefaultCurrency={isDefaultCurrency}
        installmentBreakdown={installmentBreakdown}
      />
    </Tooltip>
  </>
);

export default TermBreakdownTooltip;
